// Components
import { Stack, Skeleton, Select, Checkbox, Paper } from "@mantine/core";
import { GoalProgressForm } from "./GoalProgressForm";
import { Modal } from "@benchmark/ui/components/atomic";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormActions } from "@benchmark/ui/components/atomic";

// Hooks
import { useForm } from "@mantine/form";
import { useMedplum } from "@medplum/react";
import { useCallback, useState } from "react";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useCurrentPractitionerRole } from "@/hooks/shared/useCurrentPractitionerRole";

// Types
import type { Goal, Patient } from "@medplum/fhirtypes";
import type { GoalProgressFormData } from "@benchmark/core/types";
import type { GoalAchievementStatus } from "@benchmark/core/constants";

// Utils
import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";
import { createGoalFacade } from "@/facades/goal.facade";
import { getDateOnly } from "@benchmark/ui/utils";
import { GOAL_ACHIEVEMENT_STATUSES } from "@benchmark/core/constants";

/**
 * Props for the CloseGoalModal component.
 * @agentic connections
 */
export type CloseGoalModalProps = {
  goal: Goal;
  patient: Patient;
  onSuccess: () => void;
  onCancel: () => void;
};

/**
 * Modal for closing a goal with achievement status and optional final progress update.
 * @agentic connections
 */
export const CloseGoalModal = ({
  goal,
  patient,
  onSuccess,
  onCancel,
}: CloseGoalModalProps) => {
  const medplum = useMedplum();
  const goalFacade = createGoalFacade(medplum);
  const { practitionerRole } = useCurrentPractitionerRole();

  const [includeFinalProgress, setIncludeFinalProgress] = useState(false);
  const [finalProgressData, setFinalProgressData] =
    useState<GoalProgressFormData | null>(null);

  // Helper to convert Date to YYYY-MM-DD string
  const dateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const form = useForm<{
    achievementStatus: GoalAchievementStatus;
    statusDate: string;
  }>({
    initialValues: {
      achievementStatus: "achieved",
      statusDate: dateToString(getDateOnly(new Date())),
    },
    validate: {
      achievementStatus: (value) => (!value ? "Status is required" : null),
      statusDate: (value) => (!value ? "Date is required" : null),
    },
  });

  const { createSubmissionHandler, isSubmitting } = useFormSubmission();

  const handleSubmit = useCallback(
    (formData: {
      achievementStatus: GoalAchievementStatus;
      statusDate: string;
    }) => {
      return createSubmissionHandler(
        async (formData: {
          achievementStatus: GoalAchievementStatus;
          statusDate: string;
        }) => {
          if (!practitionerRole) {
            showErrorNotification("Missing practitioner information.");
            return;
          }

          if (!goal.id) {
            showErrorNotification("Goal must have an ID.");
            return;
          }

          // Validate nested form data if final progress is included
          if (includeFinalProgress) {
            if (!finalProgressData) {
              showErrorNotification(
                "Final progress data is required when including final progress update.",
              );
              return;
            }

            // Validate required fields
            if (!finalProgressData.effectiveDateTime) {
              showErrorNotification("Progress date is required.");
              return;
            }

            if (
              finalProgressData.gasScore === undefined ||
              finalProgressData.gasScore === null
            ) {
              showErrorNotification("GAS score is required.");
              return;
            }

            if (
              finalProgressData.gasScore < -2 ||
              finalProgressData.gasScore > 2
            ) {
              showErrorNotification("GAS score must be between -2 and +2.");
              return;
            }
          }

          try {
            // Close the goal
            await goalFacade.close(
              goal,
              formData.achievementStatus,
              patient,
              formData.statusDate,
            );

            // Optionally record final progress
            if (includeFinalProgress && finalProgressData) {
              try {
                await goalFacade.recordProgress(
                  finalProgressData,
                  patient,
                  practitionerRole,
                );
              } catch (progressError) {
                // Log but don't fail the close operation
                console.error("Error recording final progress:", progressError);
              }
            }

            showSuccessNotification("Goal closed successfully.");
            onSuccess();
          } catch (error) {
            showErrorNotification(error, {
              title: "Unable to Close Goal",
            });
          }
        },
      )(formData);
    },
    [
      practitionerRole,
      goalFacade,
      goal,
      patient,
      includeFinalProgress,
      finalProgressData,
      onSuccess,
      createSubmissionHandler,
    ],
  );

  if (!practitionerRole) {
    return <Skeleton height={200} />;
  }

  // Prepare achievement status options
  const achievementStatusOptions = GOAL_ACHIEVEMENT_STATUSES.map((status) => ({
    value: status,
    label: status.charAt(0).toUpperCase() + status.slice(1).replace(/-/g, " "),
  }));

  return (
    <Modal
      title={`Close Goal: ${goal.category?.[0]?.text || ""}`}
      opened={true}
      onClose={onCancel}
      size="lg"
      preventClose={isSubmitting}
    >
      <form
        aria-label="Close goal form"
        onSubmit={form.onSubmit(handleSubmit, (_validationErrors) => {
          showErrorNotification(
            "Please fix the form errors before submitting.",
          );
        })}
      >
        <Stack gap="md">
          {/* Goal Context */}

          <FormField
            label="Achievement Status"
            required
            aria-label="Achievement status"
          >
            <Select
              placeholder="Select achievement status"
              data={achievementStatusOptions}
              value={form.values.achievementStatus}
              onChange={(value) => {
                form.setFieldValue(
                  "achievementStatus",
                  (value || "achieved") as GoalAchievementStatus,
                );
              }}
              searchable={false}
              clearable={false}
              aria-label="Achievement status"
            />
          </FormField>

          <FormField label="Status Date" required aria-label="Status date">
            <input
              type="date"
              value={form.values.statusDate}
              onChange={(event) => {
                form.setFieldValue("statusDate", event.target.value);
              }}
              aria-label="Status date"
              style={{
                padding: "var(--mantine-spacing-xs)",
                border: "1px solid var(--mantine-color-gray-4)",
                borderRadius: "var(--mantine-radius-sm)",
              }}
            />
          </FormField>

          <FormField
            label="Final Progress Update"
            description="Optionally record a final progress update when closing this goal"
          >
            <Checkbox
              label="Include final progress update"
              checked={includeFinalProgress}
              onChange={(event) => {
                setIncludeFinalProgress(event.currentTarget.checked);
                if (event.currentTarget.checked && !finalProgressData) {
                  // Initialize final progress data
                  setFinalProgressData({
                    goalId: goal.id || "",
                    gasScore: 0,
                    effectiveDateTime: new Date().toISOString(),
                    note: "",
                  });
                }
              }}
            />
          </FormField>

          {includeFinalProgress && finalProgressData && (
            <Paper p="md" withBorder radius="sm">
              <GoalProgressForm
                goal={goal}
                patientId={patient.id || ""}
                nested={true}
                onFormDataChange={(formData) => {
                  setFinalProgressData(formData);
                }}
                onSuccess={() => {
                  // Not used in nested mode - submission handled by parent
                }}
                onCancel={() => {
                  setIncludeFinalProgress(false);
                  setFinalProgressData(null);
                }}
              />
            </Paper>
          )}

          <FormActions
            isSubmitting={isSubmitting}
            resourceType="Goal Closure"
            isEditing={false}
            showCancel={true}
            onCancel={onCancel}
            submitText="Close Goal"
          />
        </Stack>
      </form>
    </Modal>
  );
};
